import { baseApiAuthenticated } from '@/api/v2/index';
import { urlBuilder } from '@/api/utils';
import { BackendType, FrontendType, ObjectType } from '@/models/general';
import { convertOrderToFrontend, Order } from '@/models/order';
import { fetchAuthWrapper } from '@starjack/api';
import { convertKeycardToFrontend, Keycard } from '@/models/keycard';

const baseUrl = urlBuilder('order', 'orders');

type GetOrdersResponse = {
    orders: Order<BackendType>[];
};

type GetOrderResponse = Order<BackendType>;

type GetIdentificationsResponse<T extends ObjectType = FrontendType> = {
    // these unknown[] are not further typed because not yet needed in project
    barcodes: unknown[];
    pickup: unknown[];

    keycards: Keycard<T>[];
};

export const orderAPi = baseApiAuthenticated
    .enhanceEndpoints({ addTagTypes: ['ORDERS'] })
    .injectEndpoints({
        endpoints: (builder) => ({
            getOrders: builder.query<Order[], { customerId: string }>({
                query: ({ customerId }) => `${baseUrl}?userId=${customerId}`,

                transformResponse: (response: GetOrdersResponse) => {
                    return response.orders.map(convertOrderToFrontend);
                },

                providesTags: ['ORDERS'],
            }),

            getOrder: builder.query<
                Order,
                { customerId: string; orderId: string }
            >({
                query: ({ customerId, orderId }) =>
                    `${baseUrl}/${orderId}?userId=${customerId}`,

                transformResponse: (response: GetOrderResponse) => {
                    return convertOrderToFrontend(response);
                },
            }),

            getIdentifications: builder.query<
                GetIdentificationsResponse,
                { customerId: string }
            >({
                query: ({ customerId }) =>
                    `${baseUrl}/identifications?userId=${customerId}`,

                transformResponse: (
                    response: GetIdentificationsResponse<BackendType>
                ) => {
                    return {
                        ...response,
                        keycards: response.keycards.map(
                            convertKeycardToFrontend
                        ),
                    };
                },
            }),

            postReOrderKeycardsInternal: builder.mutation<
                void,
                { orderId: string }
            >({
                query: ({ orderId }) =>
                    `${baseUrl}/${orderId}/reorder-keycards-internal`,
            }),
        }),
    });

export const getInvoice = async (
    orderId: string
): Promise<[response: string, error: number]> => {
    const response = await fetchAuthWrapper(`${baseUrl}/${orderId}/invoice`);

    if (response.ok) {
        return [URL.createObjectURL(await response.blob()), null];
    }

    return [null, response.status];
};

export const {
    useGetOrdersQuery,
    useGetOrderQuery,
    usePostReOrderKeycardsInternalMutation,
    useGetIdentificationsQuery,
} = orderAPi;
